import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { PageHeader, SectionCard, StatePill, KpiTile } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth, isAdmin } from "../context/AuthContext";
import { useWS } from "../context/WebSocketContext";
import { ArrowRight, ChevronDown, Download } from "lucide-react";
import { downloadApiFile } from "../lib/downloadApiFile";
import {
  PurchaseBriefDialog,
  VoucherBriefDialog,
} from "../components/purchase/FinancialAdjustmentBriefDialogs";

function batchStatusLabel(status) {
  if (status === "UNLOADING_ACTIVE") return "Unloading";
  if (status === "MILLING_ONLY") return "Milling";
  if (status === "ARCHIVED_COMPLETED") return "Archived";
  if (status === "PLANNED") return "Planned";
  return status || "—";
}

function qtl(n) {
  const v = Number(n);
  return Number.isFinite(v) ? v.toFixed(2) : "—";
}

function pct(n) {
  const v = Number(n);
  return Number.isFinite(v) ? `${v.toFixed(2)}%` : "—";
}

function money(n) {
  const v = Number(n);
  return Number.isFinite(v) ? `₹${v.toFixed(2)}` : "—";
}

const YIELD_TABS = [
  { id: "input", label: "Input Details" },
  { id: "output", label: "Output Details" },
  { id: "lab", label: "Lab report of input" },
  { id: "voucher", label: "VCH detail of input" },
  { id: "summary", label: "Summary of the yield" },
];

export default function ProductionPage() {
  const { user } = useAuth();
  const adminUser = isAdmin(user);
  const { subscribe } = useWS();

  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [extract, setExtract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingExtract, setLoadingExtract] = useState(false);
  const [activeTab, setActiveTab] = useState("input");
  const [exporting, setExporting] = useState(false);
  const [purchasePreview, setPurchasePreview] = useState(null);
  const [voucherPreview, setVoucherPreview] = useState(null);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/production/batches");
      const rows = data?.results || [];
      setBatches(rows);
      setBatchId((prev) => {
        if (prev && rows.some((b) => String(b.id) === String(prev))) return prev;
        const prefer =
          rows.find((b) => b.status === "UNLOADING_ACTIVE")
          || rows.find((b) => b.status === "MILLING_ONLY")
          || rows[0];
        return prefer ? String(prefer.id) : "";
      });
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadExtract = useCallback(async (id) => {
    if (!id) {
      setExtract(null);
      return;
    }
    setLoadingExtract(true);
    try {
      const { data } = await api.get(`/production/batches/${id}/extract`);
      setExtract(data);
    } catch (e) {
      toast.error(extractError(e));
      setExtract(null);
    } finally {
      setLoadingExtract(false);
    }
  }, []);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  useEffect(() => {
    loadExtract(batchId);
  }, [batchId, loadExtract]);

  useEffect(() => {
    const unsubs = [
      subscribe("production", () => loadExtract(batchId)),
      subscribe("production_batch", loadBatches),
      subscribe("purchase", () => loadExtract(batchId)),
      subscribe("unloading", () => loadExtract(batchId)),
    ];
    return () => unsubs.forEach((u) => u?.());
  }, [subscribe, batchId, loadBatches, loadExtract]);

  const exportReport = async (mode) => {
    if (!batchId) {
      toast.error("Select a batch first");
      return;
    }
    setExporting(true);
    try {
      const code = extract?.batch?.code || batchId;
      const stamp = Date.now();
      if (mode === "excel") {
        await downloadApiFile(
          `/production/batches/${batchId}/extract/export/excel`,
          `yield-log-${code}-${stamp}.xlsx`,
        );
        toast.success("Excel downloaded (all 5 sheets)");
      } else {
        await downloadApiFile(
          `/production/batches/${batchId}/extract/export/pdf`,
          `yield-log-${code}-${stamp}.pdf`,
        );
        toast.success("PDF downloaded (all 5 sections)");
      }
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setExporting(false);
    }
  };

  const input = extract?.input;
  const output = extract?.output;
  const lab = extract?.lab;
  const vouchers = extract?.vouchers;
  const batch = extract?.batch;
  const kpis = extract?.kpis;

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="production-page">
      <PageHeader
        eyebrow="Production Floor"
        title="Yield log"
        description="Per-batch extract — input, output, lab, vouchers (material + freight), and yield summary. Export downloads one file with all five sections."
        actions={
          <div className="flex flex-wrap gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  disabled={!batchId || exporting}
                  data-testid="yield-log-export"
                >
                  <Download className="h-3.5 w-3.5" />
                  {exporting ? "Exporting…" : "Export"}
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => exportReport("excel")}
                  data-testid="yield-log-export-excel"
                >
                  Export Excel (all 5 sheets)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => exportReport("pdf")}
                  data-testid="yield-log-export-pdf"
                >
                  Export PDF (all 5 sections)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button asChild className="bg-bansal-sage hover:bg-bansal-sageDark text-white" size="sm">
              <Link to="/production/packing-register">Packing register</Link>
            </Button>
            {adminUser ? (
              <>
                <Button asChild variant="outline" size="sm">
                  <Link to="/production/batches">Batch controller</Link>
                </Button>
                <Button asChild variant="outline" size="sm">
                  <Link to="/production/batch-masters">Masters</Link>
                </Button>
              </>
            ) : null}
          </div>
        }
      />

      <SectionCard title="Select batch" testId="batch-extract-picker">
        {loading ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Loading batches…</p>
        ) : batches.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            No batches yet. {adminUser ? <Link to="/production/batches" className="text-primary underline">Plan a batch</Link> : "Ask admin to plan a batch."}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Batch</Label>
              <Select value={batchId} onValueChange={setBatchId}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select batch" /></SelectTrigger>
                <SelectContent>
                  {batches.map((b) => (
                    <SelectItem key={b.id} value={String(b.id)}>
                      {b.code} · {b.crop_name} · {batchStatusLabel(b.status)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {batch ? (
              <div className="flex flex-wrap items-center gap-2 pb-1">
                <p className="font-mono font-semibold">{batch.code}</p>
                <StatePill state={batch.status} />
                <span className="text-sm text-muted-foreground">{batch.crop_name} · {batch.type_name}</span>
              </div>
            ) : null}
          </div>
        )}
      </SectionCard>

      {loadingExtract ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Loading extract…</p>
      ) : extract ? (
        <>
          <div className="rounded-md border border-border bg-card px-4 py-3">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Batch record</p>
            <h2 className="text-xl font-semibold mt-0.5">{extract.title || batch?.code}</h2>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="bg-card border border-border p-1 h-auto flex-wrap w-full justify-start">
              {YIELD_TABS.map((t) => (
                <TabsTrigger key={t.id} value={t.id} className="text-xs sm:text-sm" data-testid={`yield-tab-${t.id}`}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="input" className="mt-0">
              <SectionCard title="Input Details" testId="batch-input-details" subtitle="Weights in quintal (qtl)">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                      <tr>
                        <th className="text-left py-2 px-2">Sr.</th>
                        <th className="text-left py-2 px-2">Purchase No</th>
                        <th className="text-left py-2 px-2">Vch No</th>
                        <th className="text-right py-2 px-2">Our side wt (qtl)</th>
                        <th className="text-right py-2 px-2">Bardana wt (qtl)</th>
                        <th className="text-right py-2 px-2">Paddy wt (qtl)</th>
                        <th className="text-right py-2 px-2">Net billable (qtl)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(input?.rows || []).length === 0 ? (
                        <tr>
                          <td colSpan={7} className="py-6 text-center text-muted-foreground">
                            No purchases tagged to this batch yet (unload while batch is UNLOADING_ACTIVE).
                          </td>
                        </tr>
                      ) : (
                        input.rows.map((r) => (
                          <tr key={r.purchase_id || r.sr} className="border-b border-border/50">
                            <td className="py-2 px-2">{r.sr}</td>
                            <td className="py-2 px-2">
                              {r.purchase_id ? (
                                <button
                                  type="button"
                                  className="font-mono link-brand text-left"
                                  onClick={() => setPurchasePreview({
                                    id: r.purchase_id,
                                    code: r.purchase_code,
                                  })}
                                >
                                  {r.purchase_code || `#${r.purchase_id}`}
                                </button>
                              ) : (
                                <span className="font-mono">{r.purchase_code || "—"}</span>
                              )}
                              {r.variety ? (
                                <span className="block text-[10px] text-muted-foreground">{r.variety}</span>
                              ) : null}
                            </td>
                            <td className="py-2 px-2">
                              {(r.vouchers || []).length === 0 ? (
                                <span className="text-muted-foreground">—</span>
                              ) : (
                                <div className="flex flex-col gap-0.5">
                                  {r.vouchers.map((v) => (
                                    <button
                                      key={v.id}
                                      type="button"
                                      className="font-mono text-xs link-brand text-left"
                                      onClick={() => setVoucherPreview({
                                        id: v.id,
                                        code: v.code,
                                        label: v.voucher_type || "MATERIAL",
                                      })}
                                    >
                                      {v.code || `#${v.id}`}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="py-2 px-2 text-right tabular">{qtl(r.our_side_weight_qtl)}</td>
                            <td className="py-2 px-2 text-right tabular">
                              {qtl(r.bardana_weight_qtl)}
                              {r.bardana_kg_per_bag != null ? (
                                <span className="block text-[10px] text-muted-foreground font-normal">
                                  {r.bags || 0} × {Number(r.bardana_kg_per_bag).toFixed(2)} kg/bag
                                </span>
                              ) : null}
                            </td>
                            <td className="py-2 px-2 text-right tabular font-medium">{qtl(r.paddy_weight_qtl)}</td>
                            <td className="py-2 px-2 text-right tabular font-medium">{qtl(r.net_billable_weight_qtl)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {(input?.rows || []).length > 0 ? (
                      <tfoot>
                        <tr className="border-t border-border bg-muted/50 font-medium">
                          <td className="py-2 px-2" colSpan={3}>Total</td>
                          <td className="py-2 px-2 text-right tabular">{qtl(input.totals.our_side_weight_qtl)}</td>
                          <td className="py-2 px-2 text-right tabular">{qtl(input.totals.bardana_weight_qtl)}</td>
                          <td className="py-2 px-2 text-right tabular">{qtl(input.totals.paddy_weight_qtl)}</td>
                          <td className="py-2 px-2 text-right tabular">{qtl(input.totals.net_billable_weight_qtl)}</td>
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                </div>
              </SectionCard>
            </TabsContent>

            <TabsContent value="output" className="mt-0">
              <SectionCard title="Output Details" testId="batch-output-details" subtitle="Weights in quintal (qtl)">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                      <tr>
                        <th className="text-left py-2 px-2">Sr.</th>
                        <th className="text-left py-2 px-2">Final Output</th>
                        <th className="text-left py-2 px-2">Product</th>
                        <th className="text-left py-2 px-2">Group</th>
                        <th className="text-left py-2 px-2">Bags</th>
                        <th className="text-right py-2 px-2">Weight (qtl)</th>
                        <th className="text-right py-2 px-2">% of billable</th>
                        <th className="text-right py-2 px-2">% of paddy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(output?.rows || []).length === 0 ? (
                        <tr>
                          <td colSpan={8} className="py-6 text-center text-muted-foreground">
                            No packaging logged for this batch.{" "}
                            <Link to="/production/packing-register" className="text-primary underline">Log packaging</Link>
                          </td>
                        </tr>
                      ) : (
                        output.rows.map((r) => (
                          <tr key={`${r.item_id}-${r.weight_per_bag}`} className="border-b border-border/50">
                            <td className="py-2 px-2">{r.sr}</td>
                            <td className="py-2 px-2 font-medium">{r.final_output}</td>
                            <td className="py-2 px-2 text-xs">{r.product_name || "—"}</td>
                            <td className="py-2 px-2 text-xs tracking-wide">{r.item_group || "—"}</td>
                            <td className="py-2 px-2 font-mono text-xs">{r.bags_label}</td>
                            <td className="py-2 px-2 text-right tabular font-medium">{qtl(r.weight_qtl)}</td>
                            <td className="py-2 px-2 text-right tabular">{pct(r.pct_of_billable)}</td>
                            <td className="py-2 px-2 text-right tabular">{pct(r.pct_of_paddy)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {(output?.rows || []).length > 0 ? (
                      <tfoot>
                        <tr className="border-t border-border bg-muted/50 font-medium">
                          <td className="py-2 px-2" colSpan={5}>Total</td>
                          <td className="py-2 px-2 text-right tabular">{qtl(output.totals.weight_qtl)}</td>
                          <td className="py-2 px-2 text-right tabular">{pct(output.totals.pct_of_billable)}</td>
                          <td className="py-2 px-2 text-right tabular">{pct(output.totals.pct_of_paddy)}</td>
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                </div>
                <p className="text-[11px] text-muted-foreground mt-3">
                  % of paddy = output ÷ (our side − bardana). % of billable = output ÷ net billable (MATERIAL voucher, else paddy after weight cut).
                </p>
              </SectionCard>
            </TabsContent>

            <TabsContent value="lab" className="mt-0">
              <SectionCard title="Lab report of input" testId="batch-lab-details" subtitle="Incoming, Relab, and Composite for batch purchases">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[1100px]">
                    <thead className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                      <tr>
                        <th className="text-left py-2 px-2">Sr.</th>
                        <th className="text-left py-2 px-2">Purchase</th>
                        <th className="text-left py-2 px-2">Phase</th>
                        <th className="text-right py-2 px-2">Moisture</th>
                        <th className="text-right py-2 px-2">Yield</th>
                        <th className="text-right py-2 px-2">Broken</th>
                        <th className="text-right py-2 px-2">Admix</th>
                        <th className="text-right py-2 px-2">Damage</th>
                        <th className="text-right py-2 px-2">Green</th>
                        <th className="text-right py-2 px-2">Immature</th>
                        <th className="text-right py-2 px-2">PR</th>
                        <th className="text-right py-2 px-2">Length</th>
                        <th className="text-left py-2 px-2">Verdict</th>
                        <th className="text-left py-2 px-2">Sampled by</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(lab?.rows || []).length === 0 ? (
                        <tr>
                          <td colSpan={14} className="py-6 text-center text-muted-foreground">
                            No lab reports for purchases in this batch.
                          </td>
                        </tr>
                      ) : (
                        lab.rows.map((r) => (
                          <tr key={`${r.purchase_id}-${r.phase}-${r.sr}`} className="border-b border-border/50">
                            <td className="py-2 px-2">{r.sr}</td>
                            <td className="py-2 px-2">
                              <span className="font-mono text-xs">{r.purchase_code || "—"}</span>
                              {r.party_name ? (
                                <span className="block text-[10px] text-muted-foreground">{r.party_name}</span>
                              ) : null}
                              {r.variety ? (
                                <span className="block text-[10px] text-muted-foreground">{r.variety}</span>
                              ) : null}
                            </td>
                            <td className="py-2 px-2 text-xs font-medium">{r.phase_label || r.phase}</td>
                            <td className="py-2 px-2 text-right tabular">{r.moisture_pct ?? "—"}</td>
                            <td className="py-2 px-2 text-right tabular">{r.yield_pct ?? "—"}</td>
                            <td className="py-2 px-2 text-right tabular">{r.broken_pct ?? "—"}</td>
                            <td className="py-2 px-2 text-right tabular">{r.admixture_pct ?? "—"}</td>
                            <td className="py-2 px-2 text-right tabular">{r.damage_pct ?? "—"}</td>
                            <td className="py-2 px-2 text-right tabular">{r.green_pct ?? "—"}</td>
                            <td className="py-2 px-2 text-right tabular">{r.immature_pct ?? "—"}</td>
                            <td className="py-2 px-2 text-right tabular">{r.pr_pct ?? "—"}</td>
                            <td className="py-2 px-2 text-right tabular">{r.length_mm ?? "—"}</td>
                            <td className="py-2 px-2 text-xs">{r.verdict || "—"}</td>
                            <td className="py-2 px-2 text-xs">{r.sampled_by || "—"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            </TabsContent>

            <TabsContent value="voucher" className="mt-0">
              <SectionCard
                title="VCH detail of input"
                testId="batch-voucher-details"
                subtitle="One row per purchase. Total landed cost is material gross plus the freight voucher payable."
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[1200px]">
                    <thead className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                      <tr>
                        <th className="text-left py-2 px-2">Sr.</th>
                        <th className="text-left py-2 px-2">Purchase</th>
                        <th className="text-left py-2 px-2">Party</th>
                        <th className="text-left py-2 px-2">Material Vch</th>
                        <th className="text-right py-2 px-2">Mat billable</th>
                        <th className="text-right py-2 px-2">Mat rate</th>
                        <th className="text-right py-2 px-2">Mat gross</th>
                        <th className="text-right py-2 px-2">Mat net</th>
                        <th className="text-left py-2 px-2">Freight Vch</th>
                        <th className="text-right py-2 px-2">Frt billable</th>
                        <th className="text-right py-2 px-2">Frt rate</th>
                        <th className="text-right py-2 px-2">Frt gross</th>
                        <th className="text-right py-2 px-2">Frt net</th>
                        <th className="text-right py-2 px-2">Total net</th>
                        <th className="text-right py-2 px-2">Total landed cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(vouchers?.rows || []).length === 0 ? (
                        <tr>
                          <td colSpan={15} className="py-6 text-center text-muted-foreground">
                            No purchases tagged to this batch.
                          </td>
                        </tr>
                      ) : (
                        vouchers.rows.map((r) => (
                          <tr key={r.purchase_id || r.sr} className="border-b border-border/50">
                            <td className="py-2 px-2">{r.sr}</td>
                            <td className="py-2 px-2">
                              <span className="font-mono text-xs">{r.purchase_code || "—"}</span>
                              {r.variety ? (
                                <span className="block text-[10px] text-muted-foreground">{r.variety}</span>
                              ) : null}
                            </td>
                            <td className="py-2 px-2 text-xs">{r.party_name || "—"}</td>
                            <td className="py-2 px-2">
                              {r.material_voucher_id ? (
                                <button
                                  type="button"
                                  className="font-mono text-xs link-brand text-left"
                                  onClick={() => setVoucherPreview({
                                    id: r.material_voucher_id,
                                    code: r.material_voucher_code,
                                    label: "MATERIAL",
                                  })}
                                >
                                  {r.material_voucher_code || `#${r.material_voucher_id}`}
                                </button>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="py-2 px-2 text-right tabular">{qtl(r.material_billable_qtl)}</td>
                            <td className="py-2 px-2 text-right tabular">
                              {r.material_effective_rate != null ? Number(r.material_effective_rate).toFixed(2) : "—"}
                            </td>
                            <td className="py-2 px-2 text-right tabular">{money(r.material_gross_amount)}</td>
                            <td className="py-2 px-2 text-right tabular font-medium">{money(r.material_net_payable)}</td>
                            <td className="py-2 px-2">
                              {r.freight_voucher_id ? (
                                <button
                                  type="button"
                                  className="font-mono text-xs text-amber-700 hover:underline text-left"
                                  onClick={() => setVoucherPreview({
                                    id: r.freight_voucher_id,
                                    code: r.freight_voucher_code,
                                    label: "FREIGHT",
                                  })}
                                >
                                  {r.freight_voucher_code || `#${r.freight_voucher_id}`}
                                </button>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="py-2 px-2 text-right tabular">{qtl(r.freight_billable_qtl)}</td>
                            <td className="py-2 px-2 text-right tabular">
                              {r.freight_effective_rate != null ? Number(r.freight_effective_rate).toFixed(2) : "—"}
                            </td>
                            <td className="py-2 px-2 text-right tabular">{money(r.freight_gross_amount)}</td>
                            <td className="py-2 px-2 text-right tabular font-medium">{money(r.freight_net_payable)}</td>
                            <td className="py-2 px-2 text-right tabular font-semibold">{money(r.net_payable)}</td>
                            <td className="py-2 px-2 text-right tabular font-semibold">{money(r.total_landed_cost)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {(vouchers?.rows || []).length > 0 ? (
                      <tfoot>
                        <tr className="border-t border-border bg-muted/50 font-medium text-xs">
                          <td className="py-2 px-2" colSpan={6}>Total</td>
                          <td className="py-2 px-2 text-right tabular">{money(vouchers.totals.material_gross_amount)}</td>
                          <td className="py-2 px-2 text-right tabular">{money(vouchers.totals.material_net_payable)}</td>
                          <td className="py-2 px-2" colSpan={3} />
                          <td className="py-2 px-2 text-right tabular">{money(vouchers.totals.freight_gross_amount)}</td>
                          <td className="py-2 px-2 text-right tabular">{money(vouchers.totals.freight_net_payable)}</td>
                          <td className="py-2 px-2 text-right tabular">{money(vouchers.totals.net_payable)}</td>
                          <td className="py-2 px-2 text-right tabular">{money(vouchers.totals.total_landed_cost)}</td>
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                </div>
              </SectionCard>
            </TabsContent>

            <TabsContent value="summary" className="mt-0 space-y-4">
              {kpis ? (
                <div className="space-y-3" data-testid="batch-extract-kpis">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <KpiTile
                      label="Milling yield %"
                      value={pct(kpis.actual_milling_yield_percentage ?? kpis.total_yield_pct_paddy)}
                      accent
                      sub={`Net vs paddy · ${pct(kpis.total_yield_pct_billable)} vs billable`}
                      testId="kpi-total-yield"
                    />
                    <KpiTile
                      label="Paddy wt"
                      value={`${qtl(kpis.paddy_weight_qtl)} qtl`}
                      sub={`Billable ${qtl(kpis.net_billable_weight_qtl)} qtl`}
                      testId="kpi-paddy"
                    />
                    <KpiTile
                      label="Gross packed"
                      value={`${qtl(kpis.gross_packed_qtl ?? kpis.total_output_qtl)} qtl`}
                      sub={
                        Number(kpis.blended_reprocessed_qtl || 0) > 0
                          ? `Net milling ${qtl(kpis.net_paddy_milled_qtl)} qtl (−${qtl(kpis.blended_reprocessed_qtl)} blend)`
                          : `${pct(kpis.total_yield_pct_billable)} of billable`
                      }
                      testId="kpi-output"
                    />
                    {(kpis.by_product || []).map((p) => (
                      <KpiTile
                        key={p.product_code || p.product_name}
                        label={`${p.product_name} %`}
                        value={pct(p.pct_of_paddy)}
                        accent
                        sub={`${qtl(p.weight_qtl)} qtl · ${pct(p.pct_of_billable)} vs billable`}
                        testId={`kpi-product-${String(p.product_code || p.product_name).toLowerCase().replace(/\s+/g, "-")}`}
                      />
                    ))}
                  </div>
                  {(kpis.by_group || []).length > 0 ? (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      {(kpis.by_group || []).map((g) => (
                        <KpiTile
                          key={`${g.product_name}-${g.group}`}
                          label={`${g.group} %`}
                          value={pct(g.pct_of_paddy)}
                          sub={`${g.product_name} · ${qtl(g.weight_qtl)} qtl`}
                          testId={`kpi-group-${String(g.group).toLowerCase().replace(/\s+/g, "-")}`}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <SectionCard title="Summary of the yield" testId="batch-yield-summary">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                  <SummaryLine label="Head rice" value={`${qtl(kpis?.head_rice_qtl)} qtl (${pct(kpis?.head_rice_pct_paddy)})`} />
                  <SummaryLine label="Broken" value={`${qtl(kpis?.broken_qtl)} qtl (${pct(kpis?.broken_pct_paddy)})`} />
                  <SummaryLine label="Rejection" value={`${qtl(kpis?.rejection_qtl)} qtl (${pct(kpis?.rejection_pct_paddy)})`} />
                  <SummaryLine
                    label="Gross packed"
                    value={`${qtl(kpis?.gross_packed_qtl ?? kpis?.total_output_qtl)} qtl · ${kpis?.gross_packed_bags ?? "—"} bags`}
                  />
                  <SummaryLine
                    label="Blended reprocess in"
                    value={
                      Number(kpis?.blended_reprocessed_qtl || 0) > 0
                        ? `${qtl(kpis.blended_reprocessed_qtl)} qtl · ${kpis.blended_reprocessed_bags || 0} bags`
                        : "—"
                    }
                  />
                  <SummaryLine
                    label="Net paddy milling out"
                    value={`${qtl(kpis?.net_paddy_milled_qtl ?? kpis?.total_output_qtl)} qtl (${pct(kpis?.actual_milling_yield_percentage ?? kpis?.total_yield_pct_paddy)})`}
                  />
                  <SummaryLine label="Material vouchers net" value={money(vouchers?.totals?.material_net_payable)} />
                  <SummaryLine label="Freight vouchers net" value={money(vouchers?.totals?.freight_net_payable)} />
                  <SummaryLine label="Total landed cost" value={money(vouchers?.totals?.total_landed_cost)} />
                  <SummaryLine label="Lab reports" value={String(lab?.totals?.reports ?? 0)} />
                </div>
                {kpis?.yield_adjustment_notes ? (
                  <p className="mt-3 text-xs text-muted-foreground border border-border rounded-md bg-muted/40 px-3 py-2">
                    {kpis.yield_adjustment_notes}
                  </p>
                ) : null}
              </SectionCard>
            </TabsContent>
          </Tabs>
        </>
      ) : null}

      <SectionCard title="Department runtime pages" testId="production-dept-links">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <DeptLink to="/production/boiler" label="Boiler" sub="Combipack operator workspace" />
          <DeptLink to="/production/parboiled" label="Parboiled" sub="Steaming & dryers" />
          <DeptLink to="/production/milling" label="Milling" sub="De-huskers & polishers" />
        </div>
      </SectionCard>

      <PurchaseBriefDialog
        open={Boolean(purchasePreview)}
        onOpenChange={(open) => !open && setPurchasePreview(null)}
        purchaseId={purchasePreview?.id}
        purchaseCode={purchasePreview?.code}
      />
      <VoucherBriefDialog
        open={Boolean(voucherPreview)}
        onOpenChange={(open) => !open && setVoucherPreview(null)}
        voucherId={voucherPreview?.id}
        voucherCode={voucherPreview?.code}
        label={voucherPreview?.label}
      />
    </div>
  );
}

function SummaryLine({ label, value }) {
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="font-medium mt-0.5 tabular">{value}</p>
    </div>
  );
}

function DeptLink({ to, label, sub }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between gap-2 rounded-md border border-border bg-card px-4 py-3 hover:border-bansal-sage/50 transition-colors"
    >
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{sub}</p>
      </div>
      <ArrowRight className="h-4 w-4 text-muted-foreground" />
    </Link>
  );
}
